import { defineStore } from 'pinia';
import QuotesAPI from 'dashboard/api/quotes';
import Auth from 'dashboard/api/auth';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import camelcaseKeys from 'camelcase-keys';

export const useQuotesStore = defineStore('quotes', {
  state: () => ({
    records: [],
    selectedQuote: null,
    uiFlags: {
      fetchingList: false,
      fetchingItem: false,
      creating: false,
      updating: false,
      deleting: false,
      sending: false,
      accepting: false,
      rejecting: false,
      resolvingDelivery: false,
    },
  }),

  getters: {
    getQuotes: state => camelcaseKeys(state.records, { deep: true }),
    getSelectedQuote: state => {
      return state.selectedQuote
        ? camelcaseKeys(state.selectedQuote, { deep: true })
        : null;
    },
    getUIFlags: state => state.uiFlags,
    getDraftQuotes: state => state.records.filter(q => q.status === 'draft'),
    getSentQuotes: state => state.records.filter(q => q.status === 'sent'),
    getAcceptedQuotes: state =>
      state.records.filter(q => q.status === 'accepted'),
  },

  actions: {
    setUIFlag(flags) {
      this.uiFlags = { ...this.uiFlags, ...flags };
    },

    async get(params = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const { data } = await QuotesAPI.get(params);
        this.records = data.payload || data;
        return data.payload || data;
      } catch (error) {
        throwErrorMessage(error);
        return [];
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    setSelectedQuote(quote) {
      this.selectedQuote = quote;
    },

    async show(id, { silent = false, commit = true } = {}) {
      if (!silent) this.setUIFlag({ fetchingItem: true });
      try {
        const { data } = await QuotesAPI.show(id);
        if (commit) this.selectedQuote = data.payload || data;
        return camelcaseKeys(data.payload || data, { deep: true });
      } catch (error) {
        throwErrorMessage(error);
        return null;
      } finally {
        if (!silent) this.setUIFlag({ fetchingItem: false });
      }
    },

    async create(quoteData) {
      this.setUIFlag({ creating: true });
      try {
        const { data } = await QuotesAPI.create(quoteData);
        const quote = data.payload || data;
        this.records.unshift(quote);
        return camelcaseKeys(quote, { deep: true });
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ creating: false });
      }
    },

    async update(id, quoteData) {
      this.setUIFlag({ updating: true });
      try {
        const { data } = await QuotesAPI.update(id, quoteData);
        const quote = data.payload || data;
        const index = this.records.findIndex(q => q.id === id);
        if (index !== -1) {
          this.records[index] = quote;
        }
        if (this.selectedQuote?.id === id) {
          this.selectedQuote = quote;
        }
        return camelcaseKeys(quote, { deep: true });
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ updating: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deleting: true });
      try {
        await QuotesAPI.delete(id);
        this.records = this.records.filter(q => q.id !== id);
        if (this.selectedQuote?.id === id) {
          this.selectedQuote = null;
        }
        return true;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ deleting: false });
      }
    },

    async sendQuote(id, method = 'conversation') {
      this.setUIFlag({ sending: true });
      try {
        const { data } = await QuotesAPI.sendQuote(id, method);
        const quote = data.payload || data;
        this.updateQuoteInStore(quote);
        return camelcaseKeys(quote, { deep: true });
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ sending: false });
      }
    },

    async accept(id) {
      this.setUIFlag({ accepting: true });
      try {
        const { data } = await QuotesAPI.accept(id);
        const quote = data.payload || data;
        this.updateQuoteInStore(quote);
        return camelcaseKeys(quote, { deep: true });
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ accepting: false });
      }
    },

    async reject(id, reason = '') {
      this.setUIFlag({ rejecting: true });
      try {
        const { data } = await QuotesAPI.reject(id, reason);
        const quote = data.payload || data;
        this.updateQuoteInStore(quote);
        return camelcaseKeys(quote, { deep: true });
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ rejecting: false });
      }
    },

    async resolveDelivery(id, deliveryId, resolution, reason = '') {
      this.setUIFlag({ resolvingDelivery: true });
      try {
        const { data } = await QuotesAPI.resolveDelivery(
          id,
          deliveryId,
          resolution,
          reason
        );
        const quote = data.payload || data;
        this.updateQuoteInStore(quote);
        return camelcaseKeys(quote, { deep: true });
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ resolvingDelivery: false });
      }
    },

    updateQuoteInStore(quote) {
      const index = this.records.findIndex(q => q.id === quote.id);
      if (index !== -1) {
        this.records[index] = quote;
      }
      if (this.selectedQuote?.id === quote.id) {
        this.selectedQuote = quote;
      }
    },

    clearSelectedQuote() {
      this.selectedQuote = null;
    },

    async download(id) {
      try {
        const accountId = window.location.pathname.split('/')[3] || '';
        const url = `/api/v1/accounts/${accountId}/quotes/${id}/download`;

        // Get auth headers
        const authData = Auth.getAuthData();
        const headers = authData
          ? {
              'access-token': authData['access-token'],
              'token-type': authData['token-type'],
              client: authData.client,
              expiry: authData.expiry,
              uid: authData.uid,
            }
          : {};

        // Fetch as blob to handle file download
        const response = await fetch(url, { headers });
        if (!response.ok) throw new Error('Download failed');

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);

        // Get quote number for filename if available
        const quote = this.records.find(q => q.id === id);
        const quoteNumber = quote?.quoteNumber || id;

        // Create temporary link to trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `Quote-${quoteNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      }
    },

    clearRecords() {
      this.records = [];
      this.selectedQuote = null;
    },
  },
});
